import { PageModule } from '../../utils/page.module';
import { Param, readUrlParameter, writeToUrl } from '../../utils/url.utils';
import { getCatalogDetailPageFromUrl, ResourceDetailPageType } from '../../api/general-api';
import { createHtmlElementFromString, formatText } from '../../utils/html.utils';
import { header } from '../header/header.module';
import { newsArchive } from './news-archive.module';

/**
 * Creates a custom detail page for the news archive.
 */
export class NewsArchiveDetailModule extends PageModule {
  /**
   * Selector that is rendered in this view.
   */
  componentSelector = '.news-archive-detail';

  /**
   * Markup for the news archive detail page
   */
  template = '<div class="news-archive-detail"></div>';

  /**
   * Markup for the loading indicators
   */
  loadingIndicatorMarkup = `<div class="indicators indicators--loading">
    <span class="indicator indicator--active"></span>
    <span class="indicator indicator--active"></span>
    <span class="indicator indicator--active"></span>
  </div>`;

  /**
   * URL to retrieve news detail pages.
   */
  newsUrl: string;

  /**
   * Setup the component with translations
   * @param translations The translations to initialize
   * @param newsUrl URL that can be used to retrieve the contents of the detail pages for news.
   */
  constructor(translations: { [key: string]: string }, newsUrl: string) {
    super();
    this.translations = translations;
    this.newsUrl = newsUrl;
  }

  /**
   * Add click listener for the back button on mobile.
   */
  addListeners(content: HTMLElement): void {
    const backButton = content.querySelector('.resources-detail-back-button');
    backButton?.removeAttribute('href');
    backButton?.addEventListener('click', () => {
      const selectedCategory = readUrlParameter(Param.CATEGORY);
      if (selectedCategory) {
        // return to the news archive page if there was a category selected before
        writeToUrl([], [Param.NEWS_DETAIL_ID]);
      } else {
        // otherwise just go back to whichever page the user visited before.
        window.history.back();
      }
    });
  }

  /**
   * Create the detail page
   */
  renderView(): void {
    // hide the mobile menu
    header.hideCategoryHeader();

    // hide the ventilator dropdown and the search bar (only on mobile)
    header.hideVentilatorsAndSearchOnMobile();

    // hide the sub nav
    header.showSubNav(false);

    // Hide the headers shadow before user scrolled
    header.setHideShadowBeforeScrolled(true);

    // set the correct back button title
    newsArchive.updateMobileMenuHeadingAndBackButton(document.querySelector('.section--header'), null, null, 'detail');

    // read which news needs to be displayed on this page
    const newsId = readUrlParameter(Param.NEWS_DETAIL_ID);

    // Show loading indicator while content is loaded
    this.pageComponent.appendChild(createHtmlElementFromString(this.loadingIndicatorMarkup));
    getCatalogDetailPageFromUrl(this.newsUrl, newsId, 'news', ResourceDetailPageType.Article).then((html) => {
      // Remove loading indicator
      this.pageComponent.firstElementChild.remove();
      // Render content
      if (html.length > 0) {
        const div = document.createElement('div');
        div.innerHTML = html;
        this.addListeners(div);
        this.pageComponent.appendChild(div);

        // render the page title (provided by the API in an empty div)
        const pageTitle = div.querySelector<HTMLElement>('#detailTitle')?.innerText || '';
        document.title = pageTitle;

        formatText(this.pageComponent);
      }
    });
  }
}
